import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../services/auth';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { subscriptionService } from '../services/subscriptionService';
import { Crown, Loader, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';

export function UserPreferences() {
  const user = useAuthStore(state => state.user);
  const { status, loading, loadSubscription } = useSubscriptionStore();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (user) {
      loadSubscription();
    }
  }, [user, loadSubscription]);

  // Handle URL parameters from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      loadSubscription(); // Refresh subscription status after successful payment
    }
  }, [searchParams, loadSubscription]);
  
  const handleUpgrade = async (billingPeriod: 'monthly' | 'yearly') => {
    if (!user) return;
    try {
      setIsLoading(true);
      await subscriptionService.createCheckoutSession(user.id, billingPeriod);
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start checkout process');
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!user) return;
    try {
      await subscriptionService.createPortalSession(user.id);
    } catch (error) {
      console.error('Error creating portal session:', error);
      toast.error('Failed to open subscription portal');
    }
  };

  const handleStartTrial = async () => {
    if (!user) return;
    try {
      setIsLoading(true);
      await subscriptionService.activateFreeTrial(user.id);
      await loadSubscription();
      toast.success('Free trial activated successfully!');
    } catch (error) {
      console.error('Error activating free trial:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to activate free trial. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-8">
      <header className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">User Preferences</h1>
        <p className="text-white/60">Manage your account and subscription settings</p>
      </header>

      <div className="space-y-6">
        {/* Subscription Section */}
        <section className="bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Crown className="text-gold-500" />
            Subscription
          </h2>

          {loading ? (
            <div className="flex items-center gap-2 text-white/60">
              <Loader className="animate-spin" />
              Loading subscription status...
            </div>
          ) : status?.isPro ? (
            <div>
              <div className="mb-4">
                <p className="text-gold-500 font-semibold">Pro Plan</p>
                <p className="text-white/60">You have access to all premium features</p>
                {status.expiresAt && (
                  <p className="text-white/60 mt-2">
                    Active until {new Date(status.expiresAt).toLocaleDateString()}
                  </p>
                )}
              </div>
              <button
                onClick={handleManageSubscription}
                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
              >
                Manage Subscription
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Monthly Plan */}
              <div className="rounded-lg border border-white/20 p-6 bg-white/5 backdrop-blur-sm">
                <h3 className="text-lg font-semibold text-white">Monthly Plan</h3>
                <div className="mt-4">
                  <span className="text-3xl font-bold text-white">$4.99</span>
                  <span className="text-white/60">/month</span>
                </div>
                <ul className="mt-6 space-y-4">
                  <li className="flex items-center">
                    <Check className="h-5 w-5 text-gold-500" />
                    <span className="ml-3 text-white/80">Unlimited AI Conversations</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-5 w-5 text-gold-500" />
                    <span className="ml-3 text-white/80">Advanced Scripture Analysis</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-5 w-5 text-gold-500" />
                    <span className="ml-3 text-white/80">Cancel Anytime</span>
                  </li>
                </ul>
                <button
                  onClick={() => handleUpgrade('monthly')}
                  disabled={isLoading}
                  className="mt-6 w-full rounded-md bg-gold-500 px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gold-400 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Processing...' : 'Subscribe Monthly'}
                </button>
              </div>

              {/* Yearly Plan */}
              <div className="rounded-lg border-2 border-gold-500 p-6 bg-white/5 backdrop-blur-sm relative">
                <div className="absolute -top-3 right-4 bg-gold-500 text-gray-900 px-3 py-1 rounded-full text-sm font-medium">
                  Save 16%
                </div>
                <h3 className="text-lg font-semibold text-white">Yearly Plan</h3>
                <div className="mt-4">
                  <span className="text-3xl font-bold text-white">$49.99</span>
                  <span className="text-white/60">/year</span>
                  <div className="text-sm text-white/60 mt-1">
                    <span className="line-through">$59.88</span>
                    <span className="ml-2">Save $9.89</span>
                  </div>
                </div>
                <ul className="mt-6 space-y-4">
                  <li className="flex items-center">
                    <Check className="h-5 w-5 text-gold-500" />
                    <span className="ml-3 text-white/80">Unlimited AI Conversations</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-5 w-5 text-gold-500" />
                    <span className="ml-3 text-white/80">Advanced Scripture Analysis</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-5 w-5 text-gold-500" />
                    <span className="ml-3 text-white/80">Cancel Anytime</span>
                  </li>
                </ul>
                <button
                  onClick={() => handleUpgrade('yearly')}
                  disabled={isLoading}
                  className="mt-6 w-full rounded-md bg-gold-500 px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gold-400 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Processing...' : 'Subscribe Yearly'}
                </button>
              </div>

              {/* Free Trial */}
              {!status?.isPro && !status?.isTrial && (
                <div className="rounded-lg border border-white/20 p-6 bg-white/5 backdrop-blur-sm">
                  <h3 className="text-lg font-semibold text-white">Free Trial</h3>
                  <div className="mt-4">
                    <p className="text-white/60">Try all Pro features free for 7 days. No credit card required.</p>
                  </div>
                  <button
                    onClick={handleStartTrial}
                    disabled={isLoading}
                    className="mt-6 w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? 'Processing...' : 'Start 7-Day Free Trial'}
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
